/**
 * studentLiveQuizViewModel — le quiz de fin de séance joué (`useEndOfLiveQuiz`) en `step` de
 * `<StudentLiveQuizPage>`. Pur.
 *
 * Une question se montre avec ses formules (`display_question`, `display_choices`), au rendu riche
 * seulement quand elle en porte une (`quizTextFormat`). La correction relit toutes les questions
 * jouées, comme celle du quiz de prérequis (`toUIQuizReviewQuestion`) : « Ta réponse » telle que l'élève l'a
 * vue, chaque réponse marquée sur son texte simple, celui qu'il a répondu.
 */
import {
    type StudentLiveQuizStep,
    type UILiveQuizQuestion,
} from "../../../../../design-system/pages/StudentLiveQuizPage"
import { shownChoices, type ApiChallengeQuestion } from "../../../../../engine/activity/correction/correctionChallenge"
import { type EndOfLiveQuizController } from "../../../../../engine/live/student/useEndOfLiveQuiz"
import { type UIQuizReviewQuestion } from "../../../ui/types/studentQuizReview"
import { toUIQuizReviewQuestion } from "../Activity/quizReviewViewModel"
import { quizTextFormat } from "../Activity/quizTextFormat"

const statementOf = (question: ApiChallengeQuestion) => question.display_question || question.question || ""

export function liveQuizQuestion(question: ApiChallengeQuestion): UILiveQuizQuestion {
    const text = statementOf(question)
    const options = shownChoices(question)
    return {
        id: question.id ?? 0,
        text,
        textFormat: quizTextFormat(text),
        options,
        optionFormats: options.map(quizTextFormat),
        correctIndex: question.correct_index as number,
    }
}

/** Une question relue : `picked`, le rang de la réponse touchée, absent si l'élève n'y a pas répondu ici. */
export function liveQuizReview(question: ApiChallengeQuestion, picked: number | undefined): UIQuizReviewQuestion {
    const options = question.choices ?? []
    const answer = picked != null ? (options[picked] ?? null) : null
    return toUIQuizReviewQuestion({
        id: question.id ?? 0,
        text: statementOf(question),
        options,
        displayOptions: shownChoices(question),
        answer,
        correctAnswer: options[question.correct_index as number] ?? null,
        result: answer == null ? "unanswered" : picked === question.correct_index ? "right" : "wrong",
    })
}

/** L'étape à montrer : `loading` tant que le quiz n'est pas là (ou, sans quiz, le temps de partir). */
export function studentLiveQuizStep(
    play: Pick<EndOfLiveQuizController, "quiz" | "questions" | "from" | "phase" | "game" | "picks" | "earned" | "saved">
): StudentLiveQuizStep {
    const { quiz, questions, from, phase, game, picks, earned, saved } = play
    if (quiz == null) return { kind: "loading" }
    const total = questions.length
    if (phase === "offer") {
        return {
            kind: "offer",
            total,
            minutes: quiz.minutes ?? 1,
            missed: quiz.missed ?? 0,
            petals: quiz.petals ?? 0,
        }
    }
    if (phase === "result") {
        return {
            kind: "result",
            right: (quiz.right ?? 0) + game.right,
            total,
            earned,
            saved,
            review: questions.slice(from).map((question, offset) => liveQuizReview(question, picks[from + offset])),
        }
    }
    const rank = from + game.index
    const question = questions[rank]
    if (question == null) return { kind: "loading" }
    return {
        kind: "question",
        index: rank,
        total,
        question: liveQuizQuestion(question),
        selected: game.selected,
        streak: game.streak,
    }
}
